import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { captureAnalyticsOncePerSession } from '../../actions/helpers/sessionAnalytics';
import {
  trackRecaptureUpsellFlashClicked,
  trackArmUpsellFlashClicked,
  trackPatientEngagementUpsellFlashClicked,
} from '../../global/analytics';
import './upsellFlash.scss';

// Dashboard upsell tiles: one tile per product the admin's org can still buy,
// in one row. Two products sit side by side at their original size. With
// more, the tiles narrow so a good part of the next one peeks in, and
// the row scrolls sideways: by trackpad / shift+wheel, or by resting the
// pointer over the thin strip at either edge, which glides the row that way
// (faster nearer the edge) until the pointer moves away. There are no arrows,
// dots or timers. Each tile is a headline, one line and a button in its own
// tone (Recapture purple, ARM ember, Patient Engagement midnight plum).
// Product decided these cannot be dismissed. Home passes only the products
// the org is eligible for.

// The hover strip is kept to roughly the peeking part of the next tile, so
// reaching for a visible tile's button never starts a scroll.
export const EDGE_ZONE_FRACTION = 0.08;
const EDGE_ZONE_MIN = 48;
const EDGE_ZONE_MAX = 110;
const MIN_SPEED = 3; // px per frame at the inner edge of the strip
const MAX_SPEED = 14; // px per frame at the very edge

export const PRODUCTS = {
  recapture: {
    id: 'recapture',
    to: '/app/recapture',
    tone: 'purple',
    ariaLabel: 'Learn more about Recapture',
    title: (
      <>
        Patient Recapture Agent,
        {' '}
        <em>automagically</em>
        {' '}
        keep your schedule full.
      </>
    ),
    subtitle: 'Books overdue patients back in. You only pay when they come back.',
    cta: 'Activate it now',
    trackClick: trackRecaptureUpsellFlashClicked,
  },
  arm: {
    id: 'arm',
    to: '/app/arm',
    tone: 'ember',
    ariaLabel: 'Learn more about ARM',
    title: (
      <>
        <em>Automated Referral Management</em>
        {' '}
        handles every referral as it comes in.
      </>
    ),
    subtitle: 'Patient texted seconds after the record arrives. You pay per scheduled referral.',
    cta: 'Get Started Today',
    trackClick: trackArmUpsellFlashClicked,
  },
  patientEngagement: {
    id: 'patientEngagement',
    to: '/app/engagement',
    tone: 'plum',
    ariaLabel: 'Learn more about Patient Engagement',
    title: (
      <>
        <em>Patient Engagement</em>
        {' '}
        reminds, confirms and surveys every patient by text.
      </>
    ),
    subtitle: 'Runs on the schedule already in your EMR. Patients reply, your staff answer from one inbox.',
    cta: 'Get Started Today',
    trackClick: trackPatientEngagementUpsellFlashClicked,
  },
};

const UpsellFlash = ({ products }) => {
  const tiles = products.flatMap((id) => (PRODUCTS[id] ? [PRODUCTS[id]] : []));
  const trackRef = useRef(null);
  const glide = useRef({ direction: 0, speed: 0, frame: null });

  const stopGlide = () => {
    const g = glide.current;
    if (g.frame !== null) window.cancelAnimationFrame(g.frame);
    g.frame = null;
    g.direction = 0;
  };

  useEffect(() => stopGlide, []);

  if (tiles.length === 0) return null;

  const step = () => {
    const g = glide.current;
    const track = trackRef.current;
    if (!track || g.direction === 0) {
      g.frame = null;
      return;
    }
    const max = track.scrollWidth - track.clientWidth;
    const next = Math.min(max, Math.max(0, track.scrollLeft + g.direction * g.speed));
    track.scrollLeft = next;
    if ((g.direction > 0 && next >= max) || (g.direction < 0 && next <= 0)) {
      g.frame = null;
      return;
    }
    g.frame = window.requestAnimationFrame(step);
  };

  const onMouseMove = (event) => {
    const track = trackRef.current;
    if (!track || track.scrollWidth <= track.clientWidth) return;
    const rect = track.getBoundingClientRect();
    const zone = Math.min(EDGE_ZONE_MAX, Math.max(EDGE_ZONE_MIN, rect.width * EDGE_ZONE_FRACTION));
    const x = event.clientX - rect.left;
    let direction = 0;
    let depth = 0;
    if (x > rect.width - zone) {
      direction = 1;
      depth = (x - (rect.width - zone)) / zone;
    } else if (x < zone) {
      direction = -1;
      depth = (zone - x) / zone;
    }
    const g = glide.current;
    if (direction === 0) {
      stopGlide();
      return;
    }
    g.direction = direction;
    g.speed = MIN_SPEED + Math.min(1, depth) * (MAX_SPEED - MIN_SPEED);
    if (g.frame === null) {
      // Hover is passive, so it counts once per session, never per glide.
      captureAnalyticsOncePerSession('upsell_flash_hover_scrolled');
      g.frame = window.requestAnimationFrame(step);
    }
  };

  return (
    <section className="upsell-flash" aria-label="New from Medsender">
      <div
        className={`upsell-flash-track${tiles.length > 2 ? ' upsell-flash-track--overflow' : ''}`}
        ref={trackRef}
        data-testid="upsell-flash-track"
        onMouseMove={onMouseMove}
        onMouseLeave={stopGlide}
      >
        {tiles.map((product) => (
          <Link
            key={product.id}
            to={product.to}
            className={`upsell-flash-tile upsell-flash-tile--${product.tone}`}
            aria-label={product.ariaLabel}
            onClick={() => product.trackClick()}
          >
            <div className="upsell-flash-title">{product.title}</div>
            <div className="upsell-flash-subtitle">{product.subtitle}</div>
            <div className="upsell-flash-foot">
              <span className="upsell-flash-cta">
                <span>{product.cta}</span>
                <CaretRight size={16} weight="bold" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

UpsellFlash.propTypes = {
  // Product ids to offer, in display order. Home passes only the products the
  // admin's org is eligible for (flag on, product not yet enabled).
  products: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(PRODUCTS))).isRequired,
};

export default UpsellFlash;
